import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Home, LucideIcon, RefreshCw } from "lucide-react";

interface ModuleHeaderProps {
  title: string | React.ReactNode;
  icon: LucideIcon;
  onBack?: () => void;
  filters?: React.ReactNode;
  subtitle?: string;
  hideBackButton?: boolean;
  /**
   * Сбросить кэш раздела и перечитать данные. Витрины ЦН кэшируются на час
   * (`screenCache`), и без этой кнопки дождаться свежих цифр можно было только
   * перелогином или сменой суток.
   */
  onRefresh?: () => void | Promise<void>;
}

export function ModuleHeader({
  title,
  icon: Icon,
  onBack,
  filters,
  subtitle,
  hideBackButton,
  onRefresh,
}: ModuleHeaderProps) {
  const [refreshing, setRefreshing] = useState(false);

  /*
   * Кнопка блокируется на время запроса: витрины ЦН считаются десятками
   * секунд, и без блокировки нетерпеливое нажатие ставит в очередь ещё один
   * такой же тяжёлый запрос к БД.
   */
  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try {
      await onRefresh();
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
      {/* Заголовок раздела — как экран домена на вебе: крупно, с плашкой иконки. */}
      <div className="flex items-center gap-3 min-w-0">
        <span className="tab-module-icon" aria-hidden>
          <Icon size={22} strokeWidth={2} />
        </span>
        <div className="min-w-0">
          <h2 className="tab-module-title">{title}</h2>
          {subtitle && <p className="tab-module-subtitle">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap justify-end">
        {filters}
        {onRefresh && (
          /*
           * VARIANT="GHOST" ОБЯЗАТЕЛЕН. `Button` склеивает классы строкой, без
           * tailwind-merge, и вариант по умолчанию приносит `bg-sequence-primary
           * text-white`. Кто победит у `text-white` и `text-cn-primary`, решает
           * порядок правил в CSS, а не в атрибуте: победил `text-white`, и кнопка
           * была белой надписью на белом фоне — пустым прямоугольником
           * (27.09.2026). В `ghost` нет ни фона, ни цвета текста — спорить нечему.
           */
          <Button
            variant="ghost"
            onClick={handleRefresh}
            disabled={refreshing}
            title="Обновить данные"
            className="flex items-center gap-2 bg-white text-cn-primary border border-gray-200 hover:bg-gray-50 disabled:opacity-60"
          >
            <RefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "Обновляем…" : "Обновить"}
          </Button>
        )}
        {!hideBackButton && onBack && (
          <Button
            onClick={onBack}
            className="flex items-center gap-2 bg-blue-400 text-white hover:bg-blue-500 border-0 ml-auto"
          >
            <Home size={16} />
            Вернуться в главную
          </Button>
        )}
      </div>
    </div>
  );
}
